import React from "react";
import { Link } from "react-router-dom";
import {
  CheckCircle2,
  MessageSquare,
  Truck,
  Headphones,
  UserCheck,
  Megaphone,
  PackageCheck,
  Wallet,
  Sparkles,
  Building2,
  LogIn,
} from "lucide-react";
import SiteHeader from "../components/SiteHeader";
import SiteFooter from "../components/SiteFooter";
import { BUSINESS_NAME, IMG_POD, waLink } from "../lib/site";

const USPS = [
  { value: "₹0", label: "Setup Cost" },
  { value: "₹0", label: "Inventory Cost" },
  { value: "₹0", label: "Processing Cost" },
];

const BENEFITS = [
  { icon: Truck, title: "Fast Pan-India Shipping", desc: "We ship from NCR — all major locations delivered in 3–6 days." },
  { icon: PackageCheck, title: "Your Branding, Our Standards", desc: "We maintain your brand standards with your custom branding on every order." },
  { icon: Megaphone, title: "Marketing Support", desc: "Get guidance and support to market and scale your clothing brand." },
  { icon: UserCheck, title: "Dedicated Manager", desc: "A dedicated account manager to handle your orders and queries." },
  { icon: Headphones, title: "Samples Available", desc: "Order samples to check quality before you go live with your brand." },
  { icon: Building2, title: "Trusted by 100+ Brands", desc: "We already manage production & fulfilment for 100+ clothing brands." },
];

const POD_PRICING = [
  {
    name: "French Terry",
    gsm: "240 GSM",
    tier: "Premium Oversized",
    rows: [
      { label: "Front + Back + Sleeve + Neck Label", price: 405 },
      { label: "Front + Back + Neck Label", price: 390 },
      { label: "Front or Back + Neck Label", price: 370 },
    ],
  },
  {
    name: "Cotton Blend",
    gsm: "180 GSM",
    tier: "Everyday / Streetwear",
    rows: [
      { label: "Front + Back + Sleeve + Neck Label", price: 345 },
      { label: "Front + Back + Neck Label", price: 330 },
      { label: "Front or Back + Neck Label", price: 310 },
    ],
  },
  {
    name: "China Cotton",
    gsm: "220 GSM",
    tier: "Affordable Oversized",
    rows: [
      { label: "Front + Back + Sleeve + Neck Label", price: 355 },
      { label: "Front + Back + Neck Label", price: 340 },
      { label: "Front or Back + Neck Label", price: 320 },
    ],
  },
];

const WALLET_PLANS = [
  {
    name: "Basic Wallet",
    recharge: "₹5,000",
    highlight: false,
    benefits: ["Access to POD Services", "Order From 1 Piece"],
  },
  {
    name: "Growth Wallet",
    recharge: "₹10,000",
    highlight: false,
    benefits: ["5% OFF", "Faster Processing"],
  },
  {
    name: "Scale Wallet",
    recharge: "₹25,000",
    highlight: true,
    benefits: [
      "5% OFF",
      "Free shipping & processing charges",
      "₹500 Bonus on recharge",
      "2 Free Sample T-Shirts",
      "Priority Production",
    ],
  },
];

export default function POD() {
  const waMsg = `Hi ${BUSINESS_NAME}, I want to start my clothing brand with your Print-on-Demand service (0 investment). Please share details.`;

  return (
    <div className="min-h-screen bg-white text-slate-900 selection:bg-slate-900 selection:text-white">
      <SiteHeader active="/POD" />

      {/* Hero */}
      <section className="relative py-16 sm:py-20 border-b border-slate-200 overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            <div className="lg:col-span-7 space-y-7">
              <div className="inline-flex items-center gap-2 px-3 py-1 bg-slate-100 border border-slate-200 text-xs font-mono tracking-wider uppercase text-slate-700">
                <Sparkles className="w-3.5 h-3.5 text-amber-500" /> Print On Demand
              </div>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.2] uppercase">
                Start Your Brand With <span className="bg-[#FFD400] text-slate-950 px-2 box-decoration-clone">₹0 Investment</span>
              </h1>
              <p className="text-lg text-slate-600 leading-relaxed max-w-2xl font-medium">
                Just manage your marketing and sales — we manage the rest. From high-quality prints to premium t-shirts, you're charged <strong className="text-slate-950">only when an order is shipped</strong>.
              </p>
              <div className="flex flex-wrap gap-3">
                <a
                  href={waLink(waMsg)}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="pod-wa-cta"
                  className="inline-flex items-center gap-2 bg-[#25D366] text-white hover:bg-[#20bd5a] text-sm font-bold uppercase tracking-wider px-8 py-4 transition-all duration-150 border border-slate-950 shadow-[4px_4px_0px_0px_rgba(15,23,42,1)] hover:translate-y-[-1px]"
                >
                  <MessageSquare className="w-4 h-4 fill-white" /> Start on WhatsApp
                </a>
                <Link
                  to="/pod-portal"
                  data-testid="pod-portal-link"
                  className="inline-flex items-center gap-2 bg-slate-950 text-white hover:bg-slate-800 text-sm font-bold uppercase tracking-wider px-8 py-4 transition-all duration-150 border-2 border-slate-950 shadow-[4px_4px_0px_0px_rgba(255,212,0,0.8)] hover:translate-y-[-1px]"
                >
                  <LogIn className="w-4 h-4" /> Brand Login
                </Link>
              </div>
            </div>

            <div className="lg:col-span-5">
              <div className="border-2 border-slate-950 p-2 sm:p-3 bg-white shadow-[8px_8px_0px_0px_rgba(255,212,0,0.6)]">
                <img src={IMG_POD} alt="Premium packed t-shirt order delivered with care" className="w-full h-auto object-cover border border-slate-200" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* USP zero-cost */}
      <section className="py-16 bg-slate-950 text-white border-b border-slate-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 mb-10">
            {USPS.map((u) => (
              <div key={u.label} className="border-2 border-slate-800 bg-slate-900 p-8 text-center">
                <p className="text-5xl font-extrabold font-mono text-[#25D366]">{u.value}</p>
                <p className="text-xs font-bold uppercase tracking-widest text-slate-300 mt-2">{u.label}</p>
              </div>
            ))}
          </div>
          <div className="bg-slate-900 border-2 border-slate-800 p-6 sm:p-8 flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <Wallet className="w-10 h-10 text-[#FFD400] shrink-0" />
              <div>
                <p className="text-xs uppercase tracking-widest font-bold text-slate-400">Get Started</p>
                <p className="text-xl sm:text-2xl font-extrabold uppercase">Start with as low as a ₹5,000 recharge</p>
              </div>
            </div>
            <a
              href={waLink(`Hi ${BUSINESS_NAME}, I'd like to start POD with a ₹5000 recharge. Please guide me.`)}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 bg-[#25D366] text-white hover:bg-[#20bd5a] text-sm font-bold uppercase tracking-wider px-6 py-3.5 border border-slate-950 transition-all whitespace-nowrap"
            >
              <MessageSquare className="w-4 h-4 fill-white" /> Recharge & Start
            </a>
          </div>
        </div>
      </section>

      {/* POD Pricing */}
      <section className="py-20 bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto text-center space-y-4 mb-12">
            <h2 className="text-3xl sm:text-4xl font-extrabold tracking-tight uppercase">POD Print Pricing</h2>
            <p className="text-slate-600 font-medium">
              All-inclusive per-piece pricing — premium t-shirt + printing + neck label. Choose your fabric and print placement.
            </p>
          </div>

          <div data-testid="pod-pricing" className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {POD_PRICING.map((f) => (
              <div key={f.name + f.gsm} className="bg-slate-50 border-2 border-slate-950 flex flex-col hover:shadow-[4px_4px_0px_0px_rgba(15,23,42,1)] transition-all">
                <div className="bg-slate-950 text-white p-5">
                  <div className="flex items-center justify-between gap-2">
                    <h3 className="text-xl font-extrabold uppercase tracking-tight leading-tight">{f.name}</h3>
                    <span className="text-xs font-mono bg-slate-800 text-slate-300 px-2 py-1 border border-slate-700 uppercase shrink-0">{f.gsm}</span>
                  </div>
                  <p className="text-[10px] font-bold uppercase tracking-widest text-[#FFD400] mt-1">{f.tier}</p>
                </div>
                <div className="divide-y divide-slate-200">
                  {f.rows.map((r) => (
                    <div key={r.label} className="flex items-center justify-between gap-3 px-5 py-4">
                      <span className="text-sm font-medium text-slate-700 leading-snug">{r.label}</span>
                      <span className="text-lg font-extrabold font-mono text-slate-950 shrink-0">₹{r.price}</span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>

          <div className="mt-6 flex flex-col items-center gap-2">
            <p className="inline-flex items-center gap-2 bg-amber-50 border-2 border-amber-300 text-amber-800 text-sm font-bold uppercase tracking-wide px-4 py-2.5">
              <PackageCheck className="w-4 h-4 shrink-0" /> + ₹15 extra for packing & processing of each order
            </p>
            <p className="text-xs font-bold uppercase tracking-wide text-slate-500">Shipping additional as per terms</p>
          </div>
        </div>
      </section>

      {/* Brand Wallet Plans */}
      <section className="py-20 bg-slate-50 border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto text-center space-y-4 mb-12">
            <span className="inline-flex items-center gap-2 text-xs font-mono tracking-wider uppercase bg-slate-200 px-3 py-1 text-slate-800">
              <Wallet className="w-3.5 h-3.5" /> Brand Wallet Plans
            </span>
            <h2 className="text-3xl sm:text-4xl font-extrabold tracking-tight uppercase">Recharge & Start Selling</h2>
            <p className="text-slate-600 font-medium">Top up your brand wallet and you're charged per order as you sell. Bigger recharges unlock better perks.</p>
          </div>

          <div data-testid="wallet-plans" className="grid grid-cols-1 md:grid-cols-3 gap-6 items-start">
            {WALLET_PLANS.map((p) => (
              <div
                key={p.name}
                className={`border-2 border-slate-950 flex flex-col ${p.highlight ? "bg-slate-950 text-white shadow-[6px_6px_0px_0px_rgba(255,212,0,0.7)] md:-translate-y-2" : "bg-white shadow-[4px_4px_0px_0px_rgba(15,23,42,1)]"}`}
              >
                <div className={`p-6 border-b ${p.highlight ? "border-slate-800" : "border-slate-200"}`}>
                  {p.highlight && (
                    <span className="inline-block bg-[#FFD400] text-slate-950 text-[10px] font-extrabold uppercase tracking-widest px-2 py-1 mb-3">Best Value</span>
                  )}
                  <h3 className="text-xl font-extrabold uppercase tracking-tight">{p.name}</h3>
                  <p className="mt-2 text-xs uppercase tracking-widest font-bold opacity-60">Recharge</p>
                  <p className="text-4xl font-extrabold font-mono mt-1">{p.recharge}</p>
                </div>
                <div className="p-6 space-y-3 flex-1">
                  {p.benefits.map((b) => (
                    <p key={b} className="flex items-start gap-2 text-sm font-medium leading-snug">
                      <CheckCircle2 className={`w-4 h-4 shrink-0 mt-0.5 ${p.highlight ? "text-[#FFD400]" : "text-slate-950"}`} /> {b}
                    </p>
                  ))}
                </div>
                <div className="p-6 pt-0">
                  <a
                    href={waLink(`Hi ${BUSINESS_NAME}, I'd like to start POD with the ${p.name} (${p.recharge} recharge). Please guide me.`)}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={`wallet-cta-${p.name.toLowerCase().replace(/\s+/g, "-")}`}
                    className={`w-full inline-flex items-center justify-center gap-2 text-sm font-bold uppercase tracking-wider px-6 py-3.5 border transition-all ${p.highlight ? "bg-[#25D366] text-white hover:bg-[#20bd5a] border-slate-950" : "bg-slate-950 text-white hover:bg-slate-800 border-slate-950"}`}
                  >
                    <MessageSquare className="w-4 h-4 fill-white" /> Choose {p.name.split(" ")[0]}
                  </a>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-20 bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto text-center space-y-4 mb-14">
            <h2 className="text-3xl sm:text-4xl font-extrabold tracking-tight uppercase">Everything Handled For You</h2>
            <p className="text-slate-600 font-medium">We take care of production, quality and fulfilment so you can focus on growing your brand.</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {BENEFITS.map((b) => {
              const Icon = b.icon;
              return (
                <div key={b.title} className="border-2 border-slate-950 bg-slate-50 p-6 space-y-3 hover:shadow-[4px_4px_0px_0px_rgba(15,23,42,1)] transition-all">
                  <div className="w-11 h-11 bg-slate-950 text-white flex items-center justify-center">
                    <Icon className="w-5 h-5" />
                  </div>
                  <h3 className="font-bold uppercase text-slate-950 leading-tight">{b.title}</h3>
                  <p className="text-sm text-slate-600 leading-relaxed font-medium">{b.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* How it works / CTA */}
      <section className="py-16 bg-slate-50 border-b border-slate-200">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center space-y-8">
          <h2 className="text-3xl sm:text-4xl font-extrabold tracking-tight uppercase">How It Works</h2>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 text-left">
            {[
              { n: "01", t: "You Sell", d: "Run your marketing & sales. Take orders on your store or social media." },
              { n: "02", t: "We Produce", d: "We print on premium t-shirts with your branding and pack the order." },
              { n: "03", t: "We Ship", d: "We dispatch from NCR. You're charged only when the order ships." },
            ].map((s) => (
              <div key={s.n} className="bg-white border-2 border-slate-950 p-6 space-y-2">
                <span className="font-mono text-xs font-extrabold bg-slate-950 text-white px-2 py-1">STEP {s.n}</span>
                <h3 className="font-extrabold uppercase text-lg pt-2">{s.t}</h3>
                <p className="text-sm text-slate-600 font-medium leading-relaxed">{s.d}</p>
              </div>
            ))}
          </div>
          <div className="flex flex-col sm:flex-row gap-3 justify-center pt-2">
            <a
              href={waLink(waMsg)}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center gap-2 bg-[#25D366] text-white hover:bg-[#20bd5a] text-sm font-bold uppercase tracking-wider px-8 py-4 border border-slate-950 shadow-[4px_4px_0px_0px_rgba(15,23,42,1)] hover:translate-y-[-1px] transition-all"
            >
              <MessageSquare className="w-4 h-4 fill-white" /> Launch My Brand
            </a>
          </div>
        </div>
      </section>

      <SiteFooter />
    </div>
  );
}
